// TODO: testar!
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Button, Alert, Carousel } from 'react-bootstrap';
import { FaTrashAlt } from 'react-icons/fa';
import allowedToDelete from '../../helpers/agenda/permissoes';
import api from '../../services/api';
import './custom-carousel.css';

const isIgnorableMediaError = error => {
  const name = error?.name;
  return name === 'AbortError' || name === 'IndexSizeError' || name === 'NotAllowedError';
};

const videoStyle = {
  width: '100%',
  maxWidth: '700px',
  maxHeight: '70vh',
  height: 'auto',
  backgroundColor: '#000',
};

export default function FileCarousel({ urls, setURLs }) {
  const { funcionario } = useSelector(state => state.funcionario);
  const medico = useSelector(state => state.doctor.profile);
  const [activeIndex, setActiveIndex] = useState(0);
  const [deleteAlert, setDeleteAlert] = useState({});
  const [controlsReady, setControlsReady] = useState(false);
  const videoRef = useRef(null);

  // Mantém activeIndex dentro do range quando a lista muda (ex.: delete / novo laudo).
  useEffect(() => {
    if (!urls?.length) {
      setActiveIndex(0);
      return;
    }

    setActiveIndex(prev => Math.min(prev, urls.length - 1));
  }, [urls]);

  useEffect(() => {
    setControlsReady(false);
  }, [activeIndex]);

  // iOS: play() rejeita AbortError/IndexSizeError se o <video> for pausado/desmontado
  // ou se o usuário interagir antes do metadata (troca de slide / scrubber).
  const handleVideoRef = useCallback(node => {
    videoRef.current = node;
    if (!node || node.dataset.abortSafePlay === 'true') {
      return;
    }

    node.dataset.abortSafePlay = 'true';
    const originalPlay = node.play.bind(node);
    node.play = () => originalPlay().catch(error => {
      if (isIgnorableMediaError(error)) {
        return undefined;
      }
      throw error;
    });
  }, []);

  const handleSelect = selectedIndex => {
    const video = videoRef.current;
    if (video) {
      try {
        if (!video.paused) {
          video.pause();
        }
      } catch (error) {
        if (!isIgnorableMediaError(error)) {
          throw error;
        }
      }
    }

    const maxIndex = Math.max((urls?.length || 1) - 1, 0);
    setActiveIndex(Math.max(0, Math.min(selectedIndex, maxIndex)));
  };

  const handleLoadedMetadata = event => {
    const duration = event.currentTarget?.duration;
    // iOS dispara IndexSizeError no scrubber nativo se duration ainda for NaN/Infinity.
    setControlsReady(Number.isFinite(duration) && duration > 0);
  };

  const handleVideoError = () => {
    toast.error('Não foi possível reproduzir este vídeo neste dispositivo.');
  };

  const deleteFile = async (e, fileKey) => {
    try {
      e.preventDefault();
      const res = await api.delete('/files', { data: { key: fileKey } });
      toast.success(res.data.message);
      setURLs(prevUrls => prevUrls.filter(url => url.Key !== fileKey));
    } catch (error) {
      toast.error(error.response.data.error);
    }
  };

  const handleDeleteAlert = (idx, value) => {
    setDeleteAlert(prev => ({ ...prev, [idx]: value }));
  };

  const renderFileContent = (url, idx) => {
    const fileUrl = url.url.data.preSignedUrl;
    const isActive = idx === activeIndex;

    // videos
    if (fileUrl.includes('.webm') || fileUrl.includes('.mp4')) {
      // Renderiza somente o vídeo do slide ativo. Evita pré-carregar
      // múltiplos <video> simultaneamente e o AbortError do play()
      // quando o slide muda antes da promise resolver.
      if (!isActive) {
        return (
          <div
            style={videoStyle}
            className="d-block"
            aria-hidden="true"
          />
        );
      }

      return (
        <div className="d-flex flex-column align-items-center w-100">
          <video
            ref={handleVideoRef}
            key={fileUrl}
            controls={controlsReady}
            playsInline
            preload="metadata"
            className="d-block"
            style={videoStyle}
            aria-label="Vídeo do exame"
            onLoadedMetadata={handleLoadedMetadata}
            onError={handleVideoError}
          >
            <source
              src={fileUrl}
              type={fileUrl.endsWith('.mp4') ? 'video/mp4' : 'video/webm'}
            />
          </video>
          {!controlsReady && (
            <span className="text-muted small mt-2" aria-live="polite">
              Carregando vídeo…
            </span>
          )}
        </div>
      );
    }

    if (fileUrl.includes('.pdf')) {
      return (
        <embed
          src={fileUrl}
          width="100%"
          height="750"
          type="application/pdf"
          style={{ maxWidth: '765px' }}
        />
      );
    }

    // images (jpg, jpeg, png, etc)
    return (
      <img
        src={fileUrl}
        alt={`Arquivo ${url?.Key?.split('/')[2]}`}
        style={{ maxHeight: '750px', maxWidth: '100%', objectFit: 'contain' }}
        className="d-block"
      />
    );
  };

  if (!urls?.length) {
    return null;
  }

  return (
    <Carousel
      activeIndex={activeIndex}
      onSelect={handleSelect}
      interval={null}
      indicators={urls.length > 1}
      controls={urls.length > 1}
      className="custom-carousel"
    >
      {urls.map((url, idx) => (
        <Carousel.Item key={url.url.data.preSignedUrl}>
          <div className="d-flex justify-content-center align-items-center position-relative mb-4">
            {renderFileContent(url, idx)}
          </div>
          <div className="text-center mb-3">
            <div className="d-flex align-items-center justify-content-center">
              <span className="text-dark">
                Arquivo {idx + 1} de {urls.length}
              </span>
              {allowedToDelete(medico, funcionario, 'TODOS') && (
                <FaTrashAlt
                  onClick={() => handleDeleteAlert(idx, true)}
                  cursor="pointer"
                  style={{ color: 'red', marginLeft: 5 }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Excluir arquivo ${idx + 1}`}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDeleteAlert(idx, true);
                    }
                  }}
                />
              )}
            </div>
          </div>

          {deleteAlert[idx] && (
            <Alert variant="danger" className="mt-2">
              <div className="d-flex justify-content-between align-items-center">
                <span>
                  Deseja realmente excluir o arquivo {url?.Key?.split('/')[2]}?
                </span>
                <div>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={e => {
                      deleteFile(e, url.Key);
                      handleDeleteAlert(idx, false);
                    }}
                  >
                    Sim
                  </Button>{' '}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => handleDeleteAlert(idx, false)}
                  >
                    Não
                  </Button>
                </div>
              </div>
            </Alert>
          )}
        </Carousel.Item>
      ))}
    </Carousel>
  );
}
